import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Award, Clock, Users } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Home = () => {
  const { t } = useLanguage();

  return (
    <div data-testid="home-page">
      {/* Team Banner */}
      <section className="bg-[#1A1A1A] py-5 border-b border-[#C9A227]/20" data-testid="team-banner">
        <div className="container-luxury">
          <p className="text-center text-white/90 text-base md:text-lg font-light tracking-wide">
            <span className="text-[#C9A227] font-serif font-medium">{t('home.teamHighlight')}</span>
            <span className="mx-2 text-[#C9A227]/40">|</span>
            {t('home.teamBanner')}
          </p>
        </div>
      </section>

      {/* Hero Split Section */}
      <section className="hero-split" data-testid="hero-section">
        {/* Particuliers Panel */}
        <Link 
          to="/particuliers" 
          className="hero-panel hero-panel-particuliers group cursor-pointer"
          data-testid="hero-particuliers"
        >
          <div className="text-center text-white">
            <p className="text-sm uppercase tracking-widest text-[#C9A227] mb-4 font-sans">{t('home.space')}</p>
            <h2 className="font-serif text-4xl md:text-5xl font-medium mb-6">{t('home.particuliers')}</h2>
            <div className="gold-line gold-line-center mb-6"></div>
            <p className="text-white/80 text-lg max-w-md mx-auto mb-8 font-light">
              {t('home.particuliersDesc')}
            </p>
            <span className="inline-flex items-center gap-2 text-[#C9A227] font-sans text-sm uppercase tracking-widest group-hover:gap-4 transition-all duration-300">
              {t('home.discover')} <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </Link>

        {/* Professionnels Panel */}
        <Link 
          to="/professionnels" 
          className="hero-panel hero-panel-professionnels group cursor-pointer"
          data-testid="hero-professionnels"
        >
          <div className="text-center text-white">
            <p className="text-sm uppercase tracking-widest text-[#C9A227] mb-4 font-sans">{t('home.space')}</p>
            <h2 className="font-serif text-4xl md:text-5xl font-medium mb-6">{t('home.professionnels')}</h2>
            <div className="gold-line gold-line-center mb-6"></div>
            <p className="text-white/80 text-lg max-w-md mx-auto mb-8 font-light">
              {t('home.professionnelsDesc')}
            </p>
            <span className="inline-flex items-center gap-2 text-[#C9A227] font-sans text-sm uppercase tracking-widest group-hover:gap-4 transition-all duration-300">
              {t('home.discover')} <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </Link>
      </section>

      {/* About Section */}
      <section className="section-luxury bg-[#FDFCF8] texture-overlay" data-testid="about-section">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="animate-fade-in-up">
              <p className="text-sm uppercase tracking-widest text-[#C9A227] mb-4 font-sans">{t('home.about')}</p>
              <h2 className="font-serif text-3xl md:text-5xl text-[#1A1A1A] mb-6">
                {t('home.aboutTitle')}
              </h2>
              <div className="gold-line mb-8"></div>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                {t('home.aboutText1')}
              </p>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                {t('home.aboutText2')}
              </p>
              <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="about-cta">
                {t('nav.contactUs')} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/30869731/pexels-photo-30869731.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" 
                alt="Excelsium Conseils Excellence" 
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-8 -left-8 bg-[#C9A227] text-white p-8 hidden md:block">
                <p className="font-serif text-4xl font-medium">10+</p>
                <p className="text-sm uppercase tracking-widest mt-2">{t('home.yearsExp')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-luxury bg-white" data-testid="features-section">
        <div className="container-luxury">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-sm uppercase tracking-widest text-[#C9A227] mb-4 font-sans">{t('home.whyUs')}</p>
            <h2 className="font-serif text-3xl md:text-5xl text-[#1A1A1A] mb-6">
              {t('home.commitment')}
            </h2>
            <div className="gold-line gold-line-center"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Feature 1 */}
            <div className="service-card text-center group" data-testid="feature-expertise">
              <div className="w-16 h-16 rounded-full border border-[#C9A227] flex items-center justify-center mx-auto mb-6 group-hover:bg-[#C9A227] transition-all duration-500">
                <Award className="w-8 h-8 text-[#C9A227] group-hover:text-white transition-colors duration-500" />
              </div>
              <h3 className="font-serif text-xl text-[#1A1A1A] mb-4">{t('home.expertise')}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {t('home.expertiseDesc')}
              </p>
            </div>

            {/* Feature 2 */}
            <div className="service-card text-center group" data-testid="feature-accompagnement">
              <div className="w-16 h-16 rounded-full border border-[#C9A227] flex items-center justify-center mx-auto mb-6 group-hover:bg-[#C9A227] transition-all duration-500">
                <Users className="w-8 h-8 text-[#C9A227] group-hover:text-white transition-colors duration-500" />
              </div>
              <h3 className="font-serif text-xl text-[#1A1A1A] mb-4">{t('home.support')}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {t('home.supportDesc')}
              </p>
            </div>

            {/* Feature 3 */}
            <div className="service-card text-center group" data-testid="feature-reactivite">
              <div className="w-16 h-16 rounded-full border border-[#C9A227] flex items-center justify-center mx-auto mb-6 group-hover:bg-[#C9A227] transition-all duration-500">
                <Clock className="w-8 h-8 text-[#C9A227] group-hover:text-white transition-colors duration-500" />
              </div>
              <h3 className="font-serif text-xl text-[#1A1A1A] mb-4">{t('home.reactivity')}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {t('home.reactivityDesc')}
              </p>
            </div>

            {/* Feature 4 */}
            <div className="service-card text-center group" data-testid="feature-confiance">
              <div className="w-16 h-16 rounded-full border border-[#C9A227] flex items-center justify-center mx-auto mb-6 group-hover:bg-[#C9A227] transition-all duration-500">
                <Shield className="w-8 h-8 text-[#C9A227] group-hover:text-white transition-colors duration-500" />
              </div>
              <h3 className="font-serif text-xl text-[#1A1A1A] mb-4">{t('home.trust')}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {t('home.trustDesc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="relative py-24 md:py-32 bg-cover bg-center bg-fixed"
        style={{ backgroundImage: 'url(https://images.pexels.com/photos/587838/pexels-photo-587838.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)' }}
        data-testid="cta-section"
      >
        <div className="absolute inset-0 bg-[#1A1A1A]/80"></div>
        <div className="container-luxury relative z-10">
          <div className="text-center text-white max-w-3xl mx-auto">
            <p className="text-sm uppercase tracking-widest text-[#C9A227] mb-4 font-sans">{t('home.ready')}</p>
            <h2 className="font-serif text-3xl md:text-5xl mb-6">
              {t('home.together')}
            </h2>
            <div className="gold-line gold-line-center mb-8"></div>
            <p className="text-white/80 text-lg mb-10">
              {t('home.ctaText')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary" data-testid="cta-contact">
                {t('nav.contactUs')}
              </Link>
              <Link to="/particuliers" className="btn-secondary text-white border-white hover:bg-white hover:text-[#1A1A1A]" data-testid="cta-services">
                {t('home.ourServices')}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
